import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import type { Brand } from '@rock/types';
import { normalizeBrandName } from '@rock/util';
import type { FC } from 'react';
import { useEffect, useMemo, useState } from 'react';

interface LogoSearchDialogProps {
    brand: Brand;
    open: boolean;
    isSearching: boolean;
    onOpenChange: (open: boolean) => void;
    onSearch: (query: string) => Promise<void>;
}

/**
 * Composes the web-image query before spending the SERP call. The whole query is editable: an
 * unquoted "{name} logo" lets Google swap in a bigger near-synonym entity (searching Polar Monkeys
 * returns Arctic Monkeys), and the operators that suppress that — quoting, `-term` — can't be
 * expressed by appending words to a fixed prefix. So the field is seeded with a quoted
 * `"{name}" logo` and the name buttons re-seed it, rather than locking the core.
 */
export const LogoSearchDialog: FC<LogoSearchDialogProps> = ({ brand, open, isSearching, onOpenChange, onSearch }) => {
    const names = useMemo(() => searchableNames(brand), [brand]);
    // searchableNames only drops names that fold to an empty key, so this fallback is unreachable in
    // practice — but canonicalName is the one name a brand always has.
    const defaultName = names[0] ?? brand.canonicalName;
    const [query, setQuery] = useState(() => seedQuery(defaultName));

    // Re-arm on every open — the alias list (and thus the default seed name) may have changed.
    useEffect(() => {
        if (!open) return;
        setQuery(seedQuery(defaultName));
    }, [open, defaultName]);

    const canSearch = !!query.trim();

    const handleSearch = () => {
        if (isSearching || !canSearch) return;
        // Close immediately rather than awaiting like BrandfetchDialog does — the SERP search plus its
        // per-result reachability probes can run for minutes, and the results land in the card below.
        onOpenChange(false);
        void onSearch(query);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Search web images</DialogTitle>
                    <DialogDescription>
                        Edit the Google Images query however you need — quotes pin the exact name, and{' '}
                        <code>-term</code> drops a wrong brand Google keeps substituting.
                    </DialogDescription>
                </DialogHeader>

                <div className="flex flex-col gap-3 py-2">
                    {names.length > 1 && (
                        <fieldset className="flex flex-wrap gap-1.5">
                            <legend className="sr-only">Reset query to a brand name</legend>
                            {names.map((name) => (
                                <Button
                                    key={name}
                                    size="sm"
                                    variant={query === seedQuery(name) ? 'default' : 'outline'}
                                    onClick={() => setQuery(seedQuery(name))}
                                >
                                    {name}
                                </Button>
                            ))}
                        </fieldset>
                    )}

                    <input
                        className="h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm shadow-sm outline-none placeholder:text-muted-foreground focus-visible:ring-1 focus-visible:ring-ring"
                        aria-label="Image search query"
                        placeholder="e.g. &quot;Polar Monkeys&quot; logo -arctic"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSearch();
                        }}
                        autoFocus
                    />
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>
                        Cancel
                    </Button>
                    <Button onClick={handleSearch} disabled={isSearching || !canSearch}>
                        Search
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

/**
 * Quoted by default: unquoted, Google Images pads a small brand's results with a bigger near-synonym
 * entity it thinks you meant (this is not spelling correction — `nfpr` doesn't suppress it). Quoting
 * costs nothing for brands that don't need it.
 */
function seedQuery(name: string): string {
    return `"${name}" logo`;
}

/**
 * The names an admin can seed the search with: display name first (the default), then each alias,
 * deduped on the brand-name fold so an all-caps alias of the display name ("AUTOPILOT") doesn't
 * repeat it — first occurrence (the proper-cased one) wins.
 */
function searchableNames(brand: Brand): string[] {
    const byKey = new Map<string, string>();
    for (const name of [brand.displayName ?? brand.canonicalName, ...brand.aliases]) {
        const key = normalizeBrandName(name).key;
        if (key && !byKey.has(key)) byKey.set(key, name);
    }
    return [...byKey.values()];
}
